// A40 — Premium upsell card. Used wherever a free-tier user attempts
// to access a premium-gated surface (Pearl persona-craft, Shadow
// Teams, Road tab, pooled model access, etc.). Single visual
// identity, single UX pattern.
//
// Free-tier UI checks the entitlement flag and renders this card
// instead of the surface. Backend routes also reject with 403
// premium_required as defence-in-depth (see A40 enforcement); the
// `usePremiumGuardedFetch` hook below catches that error shape and
// flips the calling component into upsell mode.

import { Lock } from "lucide-react";

interface Props {
  feature: string;
  headline: string;
  sub?: string;
  ctaLabel?: string;
  /** Where the upgrade CTA routes. Defaults to /billing. */
  ctaHref?: string;
  testId?: string;
}

export function UpsellCard({
  feature,
  headline,
  sub,
  ctaLabel = "Upgrade",
  ctaHref = "/billing",
  testId,
}: Props) {
  return (
    <div
      className="rounded-md border border-edge bg-surface-raised px-4 py-3"
      data-testid={testId ?? `upsell-${feature}`}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 text-muted">
          <Lock size={14} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-primary">{headline}</p>
          {sub && <p className="mt-1 text-xs text-muted">{sub}</p>}
          <a
            href={ctaHref}
            className="mt-3 inline-block rounded-md border border-accent bg-accent/10 px-2 py-1 text-[0.625rem] font-medium text-accent hover:bg-accent/20"
            data-testid={`upsell-${feature}-cta`}
          >
            {ctaLabel}
            {" →"}
          </a>
        </div>
      </div>
    </div>
  );
}

/**
 * Detect whether an error came from a backend 403 premium_required
 * response. The `request` helper throws an `ApiError` whose `body`
 * carries the structured JSON; this checks that shape.
 */
export function isPremiumRequired(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { status?: number; body?: { error?: string } | string };
  if (e.status !== 403) return false;
  const body = typeof e.body === "string" ? safeParse(e.body) : e.body;
  return body?.error === "premium_required";
}

function safeParse(s: string): { error?: string } | null {
  try {
    return JSON.parse(s) as { error?: string };
  } catch {
    return null;
  }
}
